import _ from 'lodash';

import {
  finleyAssert,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';
import {
  CreditAgreement as CreditAgreementTypes,
  Util,
} from '@finley-technologies/finley-types';

import {
  FinancialCovenantsInsightsGQLType,
  GetFinancialCovenantCalculationsQueryGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

export interface FinancialCovenantsCalculation {
  id: string;
  creditFacilityId: string; // FK reference to credit_facilities
  reportingDate: Util.FullDateStr;
  covenantKey: CreditAgreementTypes.Config.FinancialCovenant.FinancialCovenantKey; // Consider adding an enum/union list to restrict, or add validation at database layer
  limitValue: number; // financial covenant limit
  limitType: string; // financial covenant limit type, min/max
  value: number; // value from monthly report
  _config_?: any;
}

export const getFormattedFinancialCovenantCalculations = (
  data: FinancialCovenantsInsightsGQLType,
) => {
  const formattedCalculations: {
    [key: string]: FinancialCovenantsCalculation[];
  } = {};

  if (data.config.length === 0) {
    return formattedCalculations;
  }

  _.forEach(data.config, (config) => {
    formattedCalculations[config.key] = [];
  });

  _.forEach(data.financialCovenantsCalculations, (calculation) => {
    // calculations may exist for covenants that had calculations but have since been deleted from CA
    if (formattedCalculations[calculation.covenantKey]) {
      formattedCalculations[calculation.covenantKey].push({
        ...calculation,
        covenantKey: commonTypeguards.toEnum<CreditAgreementTypes.Config.FinancialCovenant.FinancialCovenantKey>(
          calculation.covenantKey,
        ),
      });
    }
  });

  return formattedCalculations;
};

// @portcov - both portcovs and fincovs currently live under the FinancialCovenant.FinancialCovenantKey config
// when portcovs are properly built out, can remove the below filters
export const parsePortfolioCovenantsFromAllCovenants = ({
  allCovenantData,
}: {
  allCovenantData: GetFinancialCovenantCalculationsQueryGQLType;
}) => {
  finleyAssert(
    allCovenantData.financialCovenantCalculations,
    'financialCovenantCalculations must exist on allCovenantData',
  );
  const allCovenants = allCovenantData.financialCovenantCalculations;
  const portfolioCovenantConfigs = _.filter(allCovenants.config, (covenant) => {
    return _.includes(
      _.values(
        CreditAgreementTypes.Config.FinancialCovenant.PortfolioCovenantKey,
      ),
      covenant.key,
    );
  });
  const portfolioCovenantCalculations = _.filter(
    allCovenants.financialCovenantsCalculations,
    (covenant) => {
      return _.includes(
        _.values(
          CreditAgreementTypes.Config.FinancialCovenant.PortfolioCovenantKey,
        ),
        covenant.covenantKey,
      );
    },
  );

  return {
    config: portfolioCovenantConfigs,
    financialCovenantsCalculations: portfolioCovenantCalculations,
  };
};

// @portcov - both portcovs and fincovs currently live under the FinancialCovenant.FinancialCovenantKey config
// when portcovs are properly built out, can remove the below filters
export const parseFinancialCovenantsFromAllCovenants = ({
  allCovenantData,
}: {
  allCovenantData: GetFinancialCovenantCalculationsQueryGQLType;
}) => {
  finleyAssert(
    allCovenantData.financialCovenantCalculations,
    'financialCovenantCalculations must exist on allCovenantData',
  );
  const allCovenants = allCovenantData.financialCovenantCalculations;
  const financialCovenantConfigs = _.filter(allCovenants.config, (covenant) => {
    return !_.includes(
      _.values(
        CreditAgreementTypes.Config.FinancialCovenant.PortfolioCovenantKey,
      ),
      covenant.key,
    );
  });
  const financialCovenantCalculations = _.filter(
    allCovenants.financialCovenantsCalculations,
    (covenant) => {
      return !_.includes(
        _.values(
          CreditAgreementTypes.Config.FinancialCovenant.PortfolioCovenantKey,
        ),
        covenant.covenantKey,
      );
    },
  );

  return {
    config: financialCovenantConfigs,
    financialCovenantsCalculations: financialCovenantCalculations,
  };
};
